import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  IconButton,
  Divider,
  CircularProgress,
  Drawer,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BugReportIcon from "@mui/icons-material/BugReport";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { Image } from "@mui/icons-material";

import ViewModuleIcon from "@mui/icons-material/ViewModule";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PublicIcon from "@mui/icons-material/Public";
import DevicesIcon from "@mui/icons-material/Devices";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeveloperWorkload from "./DeveloperWorkLoad";
import AssignmentPanel from "./Assignment";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import apiService from "../../service/Api";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdminBugAssignments,
  fetchBugAdminDashboard,
} from "../../store/slice/bug-fix/bugSlice";
import { BugPriority, BugReport } from "../../store/slice/bug-fix/types";
import { AppDispatch, RootState } from "../../store/Index";

/* =====================
   Types
===================== */

interface StatCard {
  label: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  subTitleColor: string;
}

const status_choices = {
  new: "New",
  in_progress: "In Progress",
  resolved: "Resolved",
  blocked: "Blocked",
};

const bugtype_choices = {
  ui_issue: "Something doesn’t look right",
  feature_not_working: "A feature isn’t working",
  performance: "The system feels slow",
  access_issue: "I can’t access something",
  other: "Something else",
};

const occurance_choices = {
  every_time: "Every time",
  sometimes: "Sometimes",
  once: "Just once",
};

const impact_choices = {
  minor: "Minor inconvenience",
  slowed: "Slowed me down",
  blocked: "Blocked my work",
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(date);

  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";

  return `${get("day")} ${get("month")} ${get("year")} ${get("hour")}:${get("minute")} ${get("dayPeriod")}`;
};

/* =====================
   Component
===================== */

export default function BugsAdminDashboard() {
  const [selectedBug, setSelectedBug] = React.useState<BugReport | null>(null);

  const labelColor = "rgba(148, 163, 184, 1)";
  const dispatch = useDispatch<AppDispatch>();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [priorityFilter, setPriorityFilter] = React.useState("all");
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [assigning, setAssigning] = React.useState(false);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [isRecent, setIsRecent] = React.useState<boolean>(false);
  const [isImageOpen, setIsImageOpen] = React.useState(false);
  const { assignments, adminDashboard } = useSelector(
    (state: RootState) => state.bugs,
  );

  const recentBugs = React.useMemo(() => {
    if (!adminDashboard?.recent_bugs) return [];
    return adminDashboard.recent_bugs;
  }, [adminDashboard]);

  const stats: StatCard[] = React.useMemo(() => {
    if (!adminDashboard) {
      return [
        {
          label: "Open bugs",
          value: 0,
          subtitle: "+0 Since yesterday",
          icon: (
            <BugReportIcon sx={{ color: "#007AFF", height: 24, width: 24 }} />
          ),
          color: "rgba(0, 122, 255, 0.15)",
          subTitleColor: "#8F8F8F",
        },
        {
          label: "Critical",
          value: 0,
          subtitle: "Needs immediate attention",
          icon: (
            <WhatshotIcon sx={{ color: "#FF3B30", height: 24, width: 24 }} />
          ),
          color: "rgba(255, 59, 48,0.15)",
          subTitleColor: "#FF3B30",
        },
        {
          label: "Avg Fix time",
          value: "0d",
          subtitle: "—",
          icon: (
            <AccessTimeIcon
              sx={{ color: "rgba(175, 82, 222, 1)", height: 24, width: 24 }}
            />
          ),
          color: "rgba(175, 82, 222, 0.15) ",
          subTitleColor: "#0D9488",
        },
        {
          label: "Resolved",
          value: 0,
          subtitle: "Last 7 days",
          icon: (
            <CheckCircleIcon
              sx={{ color: "rgba(13, 148, 136,1)", height: 24, width: 24 }}
            />
          ),
          color: "rgba(13, 148, 136,0.15) ",
          subTitleColor: "#8F8F8F",
        },
      ];
    }

    return [
      {
        label: "Open bugs",
        value: adminDashboard.stats.open_bugs,
        subtitle: "+2 Since yesterday",
        icon: (
          <BugReportIcon sx={{ color: "#007AFF", height: 24, width: 24 }} />
        ),
        color: "rgba(0, 122, 255, 0.15)",
        subTitleColor: "#8F8F8F",
      },
      {
        label: "Critical",
        value: adminDashboard.stats.critical_bugs,
        subtitle: "Needs immediate attention",
        icon: <WhatshotIcon sx={{ color: "#FF3B30", height: 24, width: 24 }} />,
        color: "rgba(255, 59, 48,0.15)",
        subTitleColor: "#FF3B30",
      },
      {
        label: "Avg Fix time",
        value: `${adminDashboard.stats.avg_fix_time_days}d`,
        subtitle: "↓ Improving",
        icon: (
          <AccessTimeIcon
            sx={{ color: "rgba(175, 82, 222, 1)", height: 24, width: 24 }}
          />
        ),
        color: "rgba(175, 82, 222, 0.15) ",
        subTitleColor: "#0D9488",
      },
      {
        label: "Resolved",
        value: adminDashboard.stats.resolved_last_7_days,
        subtitle: "Last 7 days",
        icon: (
          <CheckCircleIcon
            sx={{ color: "rgba(13, 148, 136,1)", height: 24, width: 24 }}
          />
        ),
        color: "rgba(13, 148, 136,0.15) ",
        subTitleColor: "#8F8F8F",
      },
    ];
  }, [adminDashboard]);

  const filteredAssignments = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return assignments.filter((assignment) => {
      if (statusFilter !== "all" && assignment.bug.status !== statusFilter) {
        return false;
      }
      if (priorityFilter !== "all" && assignment.priority !== priorityFilter) {
        return false;
      }
      if (!query) return true;

      const haystack = [
        assignment.bug.description,
        assignment.bug.problem,
        assignment.bug.id,
        assignment.bug.module_name,
        bugtype_choices[assignment.bug.bugtype],
        assignment.bug.status,
        assignment.priority,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [assignments, searchQuery, statusFilter, priorityFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredAssignments.length / rowsPerPage),
  );
  const currentPage = Math.min(page, totalPages);
  const pagedAssignments = React.useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredAssignments.slice(start, start + rowsPerPage);
  }, [filteredAssignments, currentPage, rowsPerPage]);

  React.useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter, priorityFilter, rowsPerPage]);

  React.useEffect(() => {
    if (page !== currentPage) {
      setPage(currentPage);
    }
  }, [page, currentPage]);

  // React.useEffect(() => {
  //     if (selectedBug) {
  //         setStatus(selectedBug.status);
  //     }
  // }, [selectedBug]);

  const handleAssign = async (payload: {
    bug: BugReport;
    developerId: number;
    priority: BugPriority;
  }) => {
    try {
      setAssigning(true);
      const idParts = payload.bug.id.split("-");
      await apiService.post("/api/bug-report/bug-assignments/", {
        bug_id: Number(idParts[idParts.length - 1]),
        assigned_to_id: payload.developerId,
        bug: payload.bug,
        priority: payload.priority,
      });
      dispatch(fetchBugAdminDashboard());
      dispatch(fetchAdminBugAssignments());
    } catch (error: unknown) {
      console.log(error);
    } finally {
      setAssigning(false);
    }
  };

  React.useEffect(() => {
    const load = async () => {
      await dispatch(fetchBugAdminDashboard());
      await dispatch(fetchAdminBugAssignments());
      setInitialLoading(false);
    };
    load();
  }, [dispatch]);

  const InfoItem = ({
    icon,
    label,
    value,
  }: {
    icon: React.ReactNode;
    label: string;
    value?: string;
  }) => (
    <Stack direction="row" spacing={1.5} flex={1} alignItems="flex-start">
      {icon}
      <Box>
        <Typography color={labelColor} variant="caption">
          {label}
        </Typography>
        <Typography variant="body2">{value || "-"}</Typography>
      </Box>
    </Stack>
  );
  if (initialLoading) {
    return (
      <Box
        p={3}
        bgcolor="#f6f8fc"
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Box p={3} bgcolor="#f6f8fc" minHeight="100vh">
      {/* =====================
         TOP STATS
      ===================== */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
        {stats.map((stat) => (
          <Card
            key={stat.label}
            sx={{
              flex: 1,
              borderRadius: "16px",
              borderColor: "#E3E3E3",
              boxShadow: "none",
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    bgcolor: stat.color,
                    p: 1.5,
                    borderRadius: "100%",
                    height: 50,
                    width: 50,
                  }}
                >
                  {stat.icon}
                </Box>
                <Box>
                  <Typography variant="h6">{stat.value}</Typography>
                  <Typography variant="body2">{stat.label}</Typography>
                  <Typography variant="caption" color={stat.subTitleColor}>
                    {stat.subtitle}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Dev workload */}
      <DeveloperWorkload developers={adminDashboard?.developer_workload} />

      {/* =====================
                     RECENT + WORKLOAD
                  ===================== */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} my={3}>
        <Card sx={{ flex: 3, borderRadius: "12px", boxShadow: "none" }}>
          <CardContent>
            <Typography fontWeight={600} mb={2}>
              Recent Bug Reports
            </Typography>

            <Stack
              spacing={2}
              sx={{
                maxHeight: 300,
                overflowY: "auto",
              }}
            >
              {recentBugs.length === 0 ? (
                <Box
                  p={3}
                  borderRadius={2}
                  bgcolor="#f3f6f9"
                  textAlign="center"
                  sx={{
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "center",
                    height: 250,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No recent bugs.
                  </Typography>
                </Box>
              ) : (
                recentBugs.map((bug: BugReport, index) => (
                  <Box
                    key={index}
                    p={2}
                    borderRadius={2}
                    border="1px solid"
                    borderColor={
                      bug.impact === "blocked" ? "#f44336" : "#ffb300"
                    }
                    bgcolor={bug.impact === "blocked" ? "#fdecea" : "#fff8e1"}
                    onClick={() => {
                      setSelectedBug(bug);
                      setIsRecent(true);
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ gap: "16px" }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                          p: 1,
                          height: "50px",
                          width: "50px",
                          alignItems: "center",
                        }}
                      >
                        <WhatshotIcon
                          sx={{
                            color:
                              bug.impact === "blocked" ? "#f44336" : "#ffb300",
                            height: 30,
                            width: 30,
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography fontWeight={600}>
                          {bug.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {bug.problem}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "fit-content",
                          borderRadius: "8px",
                        }}
                      >
                        <WarningIcon
                          sx={{
                            color:
                              bug.impact === "blocked" ? "#f44336" : "#ffb300",
                            height: 20,
                            width: 20,
                            mb: "6px",
                          }}
                        />

                        <Typography variant="caption" color="error">
                          {formatDateTime(bug.created_at)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                ))
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* =====================
         BUG LIST
      ===================== */}
      <Card sx={{ boxShadow: "none" }}>
        <CardContent>
          <Typography fontWeight={600} mb={2}>
            Bug List
          </Typography>

          <Stack direction="row" spacing={2} mb={2}>
            <TextField
              size="small"
              placeholder="Search bugs..."
              fullWidth
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Select
              size="small"
              value={statusFilter}
              onChange={(event) => setStatusFilter(String(event.target.value))}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="blocked">Blocked</MenuItem>
            </Select>
            <Select
              size="small"
              value={priorityFilter}
              onChange={(event) =>
                setPriorityFilter(String(event.target.value))
              }
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="all">All Priority</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </Select>
          </Stack>

          <Divider />

          <Box
            sx={{
              maxHeight: 500,
              overflowY: "auto",
            }}
          >
            <Stack spacing={2} mt={2}>
              {pagedAssignments.length === 0 ? (
                <Box
                  p={3}
                  borderRadius={2}
                  bgcolor="#f3f6f9"
                  textAlign="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    No bug assignments found.
                  </Typography>
                </Box>
              ) : (
                pagedAssignments.map((bug, index) => (
                  <Box
                    key={index}
                    p={2}
                    borderRadius={2}
                    bgcolor="#f3f6f9"
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      setSelectedBug(bug.bug);
                      setIsRecent(false);
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between">
                      <Box sx={{ flex: 1 }}>
                        <Stack
                          direction={"row"}
                          spacing={2}
                          sx={{ justifyContent: "space-between", flex: 1 }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography fontWeight={600}>
                              {bug.bug.description}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {bug.bug.id}
                            </Typography>
                          </Box>
                          <IconButton>
                            <WarningAmberIcon color="warning" />
                          </IconButton>
                        </Stack>

                        <Stack direction="row" spacing={1} mt={1}>
                          <Chip
                            label={bug.bug.module_name}
                            size="small"
                            sx={{ color: "#fff", backgroundColor: "#A2845E" }}
                          />

                          <Typography>
                            {bugtype_choices[bug.bug.bugtype]}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{
                            marginTop: 1,
                          }}
                        >
                          <Chip
                            label={status_choices[bug.bug.status]}
                            size="small"
                            sx={{
                              alignSelf: "flex-start",
                              width: "fit-content",
                              color: "rgba(255, 149, 0, 1)",
                              backgroundColor: "rgba(255, 149, 0, 0.05)",
                              border: "1px solid rgba(255, 149, 0, 1)",
                              flexWrap: "wrap",
                              marginTop: "4px",
                            }}
                          />
                          <PhotoLibraryIcon
                            sx={{
                              height: 24,
                              width: 24,
                              color: "rgba(165, 165, 165, 1)",
                            }}
                          />
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                ))
              )}
            </Stack>
          </Box>

          <Box
            mt={2}
            display="flex"
            justifyContent="flex-end"
            alignItems="flex-end"
            sx={{}}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mr: 5 }}
            >
              <Select
                size="small"
                value={rowsPerPage}
                onChange={(event) => setRowsPerPage(Number(event.target.value))}
                sx={{ minWidth: 120 }}
              >
                <MenuItem value={10}>10 / page</MenuItem>
                <MenuItem value={25}>25 / page</MenuItem>
                <MenuItem value={50}>50 / page</MenuItem>
              </Select>
            </Stack>

            <Box
              mt={2}
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={3}
            >
              <IconButton
                size="small"
                disabled={currentPage === 1}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                sx={{
                  border: "1px solid rgba(226, 232, 240, 1)",
                  borderRadius: "8px",
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>

              <Box
                sx={{
                  backgroundColor: "#724E41",
                  px: 2,
                  py: 0.5,
                  borderRadius: "8px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 32,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                  }}
                >
                  {currentPage}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                of
              </Typography>

              <Typography>{totalPages}</Typography>

              <IconButton
                size="small"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setPage((prev) => Math.min(prev + 1, totalPages))
                }
                sx={{
                  border: "1px solid rgba(226, 232, 240, 1)",
                  borderRadius: "8px",
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* =====================
         RIGHT DRAWER
      ===================== */}
      <Drawer
        anchor="right"
        open={Boolean(selectedBug)}
        onClose={() => setSelectedBug(null)}
        PaperProps={{ sx: { width: 420, p: 2 } }}
      >
        {selectedBug && (
          <>
            <Stack direction="row" justifyContent="space-between">
              <Stack spacing={2} direction={"row"}>
                <Typography
                  variant="caption"
                  color="rgba(130, 130, 130, 1)"
                  sx={{ fontSize: "14px" }}
                >
                  {selectedBug.id}
                </Typography>

                <Stack direction={"row"} spacing={0.5}>
                  <WarningIcon
                    sx={{
                      height: 20,
                      width: 20,
                      color: "rgba(255, 59, 48, 1)",
                    }}
                  />
                  <Typography sx={{ color: "rgba(255, 59, 48, 1)" }}>
                    {impact_choices[selectedBug.impact]}
                  </Typography>
                </Stack>
              </Stack>
              <IconButton onClick={() => setSelectedBug(null)}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <Typography fontWeight={600} mt={2}>
              {selectedBug.description}
            </Typography>

            <Stack my={2} spacing={5} direction="row">
              <Typography variant="subtitle2" color="text.secondary">
                STATUS
              </Typography>

              <Typography color="text.secondary">
                {status_choices[selectedBug.status]}
              </Typography>
            </Stack>

            <Divider />

            {isRecent ? (
              <AssignmentPanel
                bug={selectedBug}
                assigning={assigning}
                onAssign={handleAssign}
              />
            ) : (
              <Stack
                sx={{
                  backgroundColor: "rgba(219, 234, 254, 1)", // light blue
                  borderRadius: "16px",
                  p: 2.5,
                  width: "100%",
                  my: 3,
                }}
              >
                <Typography color="text.secondary">ASSIGNED TO</Typography>
                <Typography color="text.secondary">
                  {
                    pagedAssignments.find(
                      (bug) => bug.bug.id === selectedBug.id,
                    )?.assigned_to.first_name
                  }{" "}
                  {
                    pagedAssignments.find(
                      (bug) => bug.bug.id === selectedBug.id,
                    )?.assigned_to.last_name
                  }
                </Typography>
              </Stack>
            )}

            {/* 
                        <Stack
                            sx={{
                                backgroundColor: "rgba(204, 230, 255, 1)",
                                flex: 1,
                                p: 1.5,
                                my: 2,

                                borderTop: "2px solid rgba(95, 177, 255, 1)",
                                borderBottom: "3px solid rgba(95, 177, 255, 1)",
                            }}
                            spacing={1}
                        >

                            <Typography sx={{ fontWeight: "600px", fontSize: "14px" }}>SMART SUMMARY</Typography>
                            <Typography >Graphics upload button unresponsive in Bookings module. User blocked from completing task.</Typography>
                        </Stack> */}

            <Typography>AUTO CAPTURED CONTEXT</Typography>

            <Stack spacing={3} mt={1}>
              {/* Row 1 */}
              <Stack direction="row" spacing={4}>
                <InfoItem
                  icon={<PhotoLibraryIcon sx={{ color: labelColor }} />}
                  label="Page"
                  value={selectedBug.page}
                />
                <InfoItem
                  icon={<ViewModuleIcon sx={{ color: labelColor }} />}
                  label="Module"
                  value={selectedBug.module_name}
                />
              </Stack>

              {/* Row 2 */}
              <Stack direction="row" spacing={4}>
                <InfoItem
                  icon={<PersonOutlineIcon sx={{ color: labelColor }} />}
                  label="Reporter Role"
                  value={selectedBug.reporter_role}
                />
                <InfoItem
                  icon={<PublicIcon sx={{ color: labelColor }} />}
                  label="Browser"
                  value={selectedBug.browser}
                />
              </Stack>

              {/* Row 3 */}
              <Stack direction="row" spacing={4}>
                <InfoItem
                  icon={<DevicesIcon sx={{ color: labelColor }} />}
                  label="Device"
                  value={selectedBug.device}
                />
                <InfoItem
                  icon={<AspectRatioIcon sx={{ color: labelColor }} />}
                  label="Screen Resolution"
                  value={selectedBug.screen}
                />
              </Stack>

              {/* Row 4 */}
              <Stack direction="row" spacing={4}>
                <InfoItem
                  icon={<InfoOutlinedIcon sx={{ color: labelColor }} />}
                  label="App Version"
                  value={selectedBug.version}
                />
                {/* Empty column keeps alignment */}
                <Box flex={1} />
              </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography>USER REPORT</Typography>
            <Stack spacing={2} mt={1}>
              <Stack spacing={1}>
                <Typography color="rgba(148, 163, 184, 1)">
                  Issue Type:
                </Typography>
                <Typography>{bugtype_choices[selectedBug.bugtype]}</Typography>
              </Stack>

              <Stack spacing={1}>
                <Typography color="rgba(148, 163, 184, 1)">
                  What they were trying to do:
                </Typography>
                <Typography>{selectedBug.description}</Typography>
              </Stack>

              <Stack spacing={1}>
                <Typography color="rgba(148, 163, 184, 1)">
                  What went wrong:
                </Typography>
                <Typography>{selectedBug.problem}</Typography>
              </Stack>

              <Stack direction={"row"} spacing={5}>
                <Stack spacing={0.5}>
                  <Typography color="rgba(148, 163, 184, 1)">
                    Reproducibility:
                  </Typography>
                  <Typography>
                    {occurance_choices[selectedBug.occurence]}
                  </Typography>
                </Stack>
                <Stack spacing={0.5}>
                  <Typography color="rgba(148, 163, 184, 1)">
                    Impact:
                  </Typography>
                  <Typography>{impact_choices[selectedBug.impact]}</Typography>
                </Stack>
              </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography>VISUAL EVIDENCE</Typography>

            <Button
              variant="outlined"
              size="medium"
              disabled={!selectedBug.image}
              onClick={() => setIsImageOpen(true)}
              sx={{
                backgroundColor: "rgba(240, 244, 248, 1)",
                border: "1px rgba(227, 227, 227, 1) ",
                borderRadius: "26px",
                gap: 1.5,
                color: "#434546",
                py: 2,
                alignSelf: "flex-start",
                px: 4,
                mt: 2,
              }}
            >
              <Image
                sx={{
                  height: 20,
                  width: 20,
                }}
              />

              <Typography>View Screenshot</Typography>
            </Button>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1}>
              <Typography>TIMELINE</Typography>

              <Typography color="rgba(148, 163, 184, 1)">
                Reported {formatDateTime(selectedBug.created_at)}
              </Typography>
            </Stack>

            <Drawer
              anchor="bottom"
              open={isImageOpen}
              onClose={() => setIsImageOpen(false)}
              PaperProps={{
                sx: { p: 2, borderTopLeftRadius: 16, borderTopRightRadius: 16 },
              }}
            >
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography fontWeight={600}>Screenshot</Typography>
                  <IconButton onClick={() => setIsImageOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {selectedBug.image ? (
                    <Box
                      component="img"
                      src={
                        typeof selectedBug.image === "string"
                          ? selectedBug.image
                          : URL.createObjectURL(selectedBug.image)
                      }
                      alt="Bug screenshot"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: "70vh",
                        borderRadius: 2,
                      }}
                    />
                  ) : (
                    <Typography color="text.secondary">
                      No screenshot available.
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Drawer>
          </>
        )}
      </Drawer>
    </Box>
  );
}
